import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useFilter } from "@/context/FilterContext";
import { CATEGORY_GROUPS, matchesGroup } from "@/components/Products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

export default function CategoriesGrid() {
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const { setCategory } = useFilter();

  const groups = Object.keys(CATEGORY_GROUPS)
    .map((name) => {
      const items = products.filter((p) => matchesGroup(p, name));
      return items.length
        ? { name, count: items.length, image: items[0].image }
        : null;
    })
    .filter(Boolean);

  const handleSelect = (name) => {
    setCategory(name);
    if (window.__lenis) {
      window.__lenis.scrollTo("#shop", { offset: -64, duration: 1.4 });
    } else {
      document.querySelector("#shop")?.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!groups.length) return null;

  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(pointer: coarse)").matches;

  return (
    <section
      data-testid="categories-section"
      className="mx-auto max-w-[1600px] px-5 py-10 md:px-12 md:py-14"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className="mb-8 md:mb-12"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-smoke">
          Find your thing
        </p>
        <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl md:text-6xl">
          Shop by <span className="italic text-ember">category</span>.
        </h2>
      </motion.div>

      <div className="grid grid-cols-2 gap-3.5 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
        {groups.map((group, i) => (
          <motion.button
            key={group.name}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: isTouch ? 0 : (i % 4) * 0.08, ease: EASE }}
            onClick={() => handleSelect(group.name)}
            data-testid={`category-tile-${group.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group relative aspect-square overflow-hidden bg-white text-left"
          >
            <img
              src={group.image}
              alt={group.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent transition-colors duration-500 group-hover:from-ember/70" />
            <ArrowUpRight
              className="absolute right-3 top-3 h-4 w-4 text-bone opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100 sm:right-4 sm:top-4 sm:h-5 sm:w-5"
              strokeWidth={1.5}
            />
            <div className="absolute bottom-0 left-0 p-3.5 sm:p-5">
              <p className="font-serif text-xl font-medium tracking-tight text-bone drop-shadow-sm sm:text-2xl md:text-3xl">
                {group.name}
              </p>
              <p className="mt-0.5 text-[9px] font-medium uppercase tracking-[0.2em] text-bone/90 sm:mt-1 sm:text-[10px]">
                {group.count} {group.count === 1 ? "good" : "goods"}
              </p>
            </div>
          </motion.button>
        ))}
      </div>
    </section>
  );
}
